import dayjs from 'dayjs';

import type { FilterChipDateRangePreset } from './types';

export const CUSTOM_DATE_PRESET = 'custom';

export interface DateRangeValue {
  from: Date | null;
  to: Date | null;
}

/**
 * Default preset list shown in the date-range chip popover.
 * Keys must stay stable — they are stored in the URL.
 */
export const defaultFilterDatePresets: FilterChipDateRangePreset[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'mtd', label: 'Month to date' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'all', label: 'All time' },
];

const ALL_TIME_FROM = new Date(2020, 0, 1);

/** Resolve a preset key to a concrete date range. Returns `null` for unknown keys. */
export const presetToRange = (preset: string): DateRangeValue | null => {
  const now = dayjs();
  const startOfToday = now.startOf('day');
  const endOfToday = now.endOf('day');

  switch (preset) {
    case 'today':
      return { from: startOfToday.toDate(), to: endOfToday.toDate() };
    case '7d':
      return { from: startOfToday.subtract(6, 'day').toDate(), to: endOfToday.toDate() };
    case '30d':
      return { from: startOfToday.subtract(29, 'day').toDate(), to: endOfToday.toDate() };
    case '90d':
      return { from: startOfToday.subtract(89, 'day').toDate(), to: endOfToday.toDate() };
    case 'mtd':
      return { from: now.startOf('month').toDate(), to: endOfToday.toDate() };
    case 'ytd':
      return { from: now.startOf('year').toDate(), to: endOfToday.toDate() };
    case 'all':
      return { from: ALL_TIME_FROM, to: endOfToday.toDate() };
    default:
      return null;
  }
};

/** Format a preset key into the chip's active label. */
export const formatPresetLabel = (
  presetValue: string,
  presets: FilterChipDateRangePreset[],
  customRange?: DateRangeValue,
): string => {
  if (presetValue === CUSTOM_DATE_PRESET && customRange?.from && customRange?.to) {
    return `${dayjs(customRange.from).format('MMM D, YYYY')} – ${dayjs(customRange.to).format('MMM D, YYYY')}`;
  }
  const preset = presets.find((p) => p.value === presetValue);
  return preset?.label ?? presetValue;
};
